import { Component, computed, effect, EventEmitter, inject, Injector, Input, model, OnChanges, OnDestroy, OnInit, Output, signal, SimpleChanges, viewChild } from '@angular/core';
import { Router } from '@angular/router';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { DashboardVisualTarget } from 'src/app/pbir/dashboards/DashboardVisualTarget';
import {
    DashboardDto,
    DashboardPreviewDetailsDto,
    DashboardVisualContentDto,
    FilterDto,
    ListUserDashboardDto,
    ListUserVisualDto,
    PublishedReportItemType,
    UserStateDto,
    VisualDto
} from 'src/app/services/api.services';
import { AssetLocks } from 'src/app/services/AssetLocks.service';
import { getInsightsPbiChartType } from 'src/app/shared/chart/chart';
import { AppUser } from 'src/app/shared/models/app-user';
import { VolatileSettingsDataService } from '../../services/data/volatile-settings-data.service';
import {
    HomeItemSelectorCategory,
    HomeItemSelectorItem,
    HomeItemSelectorSubCategory
} from '../home-item-selector/home-item-selector.component';
import { SidePanelFilterListMode, SidePanelFilterModel } from 'src/app/pbir/components/SidePanelFilterList.component';
import { DataModelResolver } from 'src/app/pbir/dataModel/DataModelResolver';
import { DataModel } from 'src/app/pbir/dataModel';
import { SidePanelComponent } from 'src/app/pbir/components/SidePanel.component';
import { toSignal } from '@angular/core/rxjs-interop';
import { DashboardEmbedComponent } from 'src/app/pbir/components/DashboardEmbed.component';
import { VisualInteractions } from 'src/app/pbir/visuals/VisualInteractions';
import { ReturnUrl } from 'src/app/services/return-url';

type DashboardListItem = ListUserDashboardDto & { visible: boolean; };
type VisualListItem = ListUserVisualDto & { visible: boolean; };

export interface DashboardDropdownValues {
  id: number;
  name: string;
  dashboardPreview: DashboardPreviewDetailsDto;
}

export interface DashboardDropdownGroup {
  groupName: string;
  iconClass: string;
  dashboards: DashboardDropdownValues[];
}

@Component({
  selector: 'app-insights-user-home',
  templateUrl: './insights-user-home.component.html',
  styleUrls: ['./insights-user-home.component.scss'],
  standalone: false
})
export class InsightsUserHomeComponent implements OnInit, OnChanges, OnDestroy {
  readonly SidePanelFilterListMode = SidePanelFilterListMode;
  readonly injector = inject(Injector);
  readonly returnUrl = inject(ReturnUrl);

  @Input() user: AppUser;

  @Input() userState: UserStateDto;

  @Input() busy = false;

  @Input() loading = false;

  @Input() loadingDashboards = false;
  @Input() dashboards: ListUserDashboardDto[] = [];

  @Input() loadingVisuals = false;
  @Input() visuals: ListUserVisualDto[] = [];

  @Input() dashboardId: number = null;
  @Input() visualId: number = null;

  @Input() loadingDashboard = false;
  @Input() dashboard: DashboardDto = null;
  @Input() visualContents: DashboardVisualContentDto[];

  @Input() loadingVisual = false;
  @Input() visual: VisualDto = null;

  @Output() copyDashboardEvent = new EventEmitter<number>();
  @Output() editDashboardEvent = new EventEmitter<{ dashboardId: number }>();
  @Output() loadDashboardEvent = new EventEmitter<number>();
  @Output() setLastViewedDashboardEvent = new EventEmitter<number>();
  @Output() addDashboardToHomeEvent = new EventEmitter<number>();

  @Output() copyVisualEvent = new EventEmitter<number>();
  @Output() loadVisualEvent = new EventEmitter<number>();

  @Output() addVisualToHomeEvent = new EventEmitter<number>();

  @Output('exportVisual') _exportVisual = new EventEmitter();
  @Output('exportToPdf') _exportToPdf = new EventEmitter<any>();

  dropdownText: string;
  dropdownCategories: HomeItemSelectorCategory[];
  dropdownItem: HomeItemSelectorItem;

  selectedDashboardId: number = 0;

  userDashboards: DashboardListItem[] = [];
  sharedDashboards: DashboardListItem[] = [];
  libraryDashboards: DashboardListItem[] = [];

  selectedVisualId: number = 0;

  userVisuals: VisualListItem[] = [];
  sharedVisuals: VisualListItem[] = [];
  libraryVisuals: VisualListItem[] = [];

  showCopyButton = false;
  showExportToPDFButton = false;
  showVisualExportButton = false;
  canEdit = false;

  anyHomeItem = false;

  itemName = 'Nothing Selected';

  userNameLabel: string;

  disableHomeButton = false;

  currentItemIsLocked = false;

  private subscriptions = new Subject<void>();
  
  readonly assetLocks = inject(AssetLocks);
  readonly dataModelResolver = inject(DataModelResolver);

  contentDataModel = toSignal(this.dataModelResolver.getOrganisationModel());
  filters = model<SidePanelFilterModel[]>([]);
  filtersAvailable = signal(false);
  filtersLoading = signal(false);
  
  sidePanelCollapse = computed(() => this.sidePanelCollapsed() || !this.filtersAvailable());
  sidePanelCollapsed = signal(true);
  
  readonly sidePanel = viewChild(SidePanelComponent);
  
  readonly dashboardEmbed = viewChild<DashboardEmbedComponent>('dashboardEmbed');
  readonly visualHosts = computed(() => this.dashboardEmbed()?.visualEmbeds()?.map(x => x.visualHost()));
  readonly visualInteractions = new VisualInteractions(this.injector, this.visualHosts);

  filtersResetClick() {
    this.filters.set(this.getInitialFilters());
  }

  filtersEditClick() {
    this.returnUrl.push(this.router.url);

    if (this.selectedDashboardId) {
      this.router.navigate(['/', 'dashboards', this.selectedDashboardId, 'edit']);
    }
    else {
      this.router.navigate(['/', 'visuals', this.selectedVisualId]);
    }
  }

  constructor(
    public router: Router,
    private volatileSettingsDataService: VolatileSettingsDataService) {
    effect(() => {
      let [filtersAvailable, sidePanel] = [this.filtersAvailable(), this.sidePanel()];
      if (sidePanel && !filtersAvailable)
        sidePanel.collapsed.set(true);
    });
  }

  ngOnInit() {
    combineLatest([
      this.assetLocks.visuals.locks$, 
      this.assetLocks.dashboards.locks$
    ]).pipe(
      map(() => this.currentItemLocked()),
      takeUntil(this.subscriptions)
    ).subscribe();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.dashboards) {
      this.categorizeDashboards();
    }
    if (changes.visuals) {
      this.categorizeVisuals();
    }

    if (changes.visuals || changes.dashboards) {
      this.anyHomeItem = !!this.dashboards.find(d => d.home) || !!this.visuals.find(v => v.home);
      this.buildDropdown();
    }


    if (changes.dashboardId || changes.visualId) {
      if (this.dashboardId) {
        this.selectedDashboardId = this.dashboardId;
        this.dropdownItem = this.getDropdownItem(this.selectedDashboardId, PublishedReportItemType.Dashboard);
        this.currentItemLocked();
      }
      else {
        this.selectedVisualId = this.visualId;
        this.dropdownItem = this.getDropdownItem(this.selectedVisualId, PublishedReportItemType.Visual);
        this.currentItemLocked();
      }
    }

    if (changes.user || changes.dashboard || changes.visual) {
      this.setCanEdit();
      this.setItemLabel();
      this.setUserNameLabel();
    }

    this.showCopyButton = this.isShowCopy();
    this.showExportToPDFButton = this.canExportToPDF();
    this.showVisualExportButton = this.canExportVisual();
    this.disableHomeButton = this.loadingDashboards || this.loadingVisuals;

    if (this.loading || this.loadingVisual || this.loadingDashboard) {
      this.dropdownText = 'Loading...'
    } else {
      this.dropdownText = this.itemName;
    }
    
    if (changes.dashboard || changes.visual) {
      this.filters.set(this.getInitialFilters());

      if (!this.dashboard && !this.visual)
        this.filtersLoading.set(true);

      if (this.visual) {
        this.filtersLoading.set(false);
        this.filtersAvailable.set(false);
      }
      else if (this.dashboard) {
        this.filtersLoading.set(false);
        this.filtersAvailable.set(true);
      }
    }
  }

  private getInitialFilters() {
    if (this.visual) {
      // We don't display filters for visuals (why? not sure)
      return [];
    }
    else if (this.dashboard) {
      return this.dashboard?.filters?.map(x => ({
        id: SidePanelFilterModel.generateId(),
        dto: x
      })) ?? [];
    }
  }


  ngOnDestroy(): void {
    this.subscriptions.next();
    this.subscriptions.complete();
  }

  private setItemLabel() {
    if (this.dashboard) {
      this.itemName = this.dashboard.name;
      return;
    }
    if (this.visual) {
      this.itemName = this.visual.name;
      return
    }
    this.itemName = 'Nothing Selected';
  }

  private setCanEdit() {
    let canEdit = false;

    let item = this.dashboard ?? this.visual;

    if (this.user && item) {
      canEdit = this.user.roles.isAdmin || (!item.fromLibrary && item.createdBy === this.user.username) || item.shared;
    }

    this.canEdit = canEdit;
  }

  private setUserNameLabel() {
    if (this.user.claims.given_name) {
      this.userNameLabel = this.user.claims.given_name;
    }
    else if (this.user.claims.name) {
      this.userNameLabel = this.user.claims.name;
    }
    else {
      this.userNameLabel = '';
    }
  }

  private categorizeDashboards() {
    this.userDashboards =
      this.dashboards
        .filter(d => !d.fromLibrary && d.createdBy === this.user.username)
        .map(d => ({ ...d, visible: true }));

    this.sharedDashboards =
      this.dashboards
        .filter(d => d.shared)
        .map(d => ({ ...d, visible: true }));

    this.libraryDashboards =
      this.dashboards
        .filter(d => d.fromLibrary)
        .map(d => ({ ...d, visible: true }));

    let sortFunction: (a: DashboardListItem, b: DashboardListItem) => number = (a, b) => {
      return a.name.toLowerCase().localeCompare(b.name);
    }

    this.userDashboards.sort(sortFunction);
    this.sharedDashboards.sort(sortFunction);
    this.libraryDashboards.sort(sortFunction);
  }

  private categorizeVisuals() {
    this.userVisuals =
      this.visuals
        .filter(d => !d.fromLibrary && d.createdBy === this.user.username)
        .map(d => ({ ...d, visible: true }));

    this.sharedVisuals =
      this.visuals
        .filter(d => d.shared)
        .map(d => ({ ...d, visible: true }));

    this.libraryVisuals =
      this.visuals
        .filter(d => d.fromLibrary)
        .map(d => ({ ...d, visible: true }));

    let sortFunction: (a: VisualListItem, b: VisualListItem) => number = (a, b) => {
      return a.name.toLowerCase().localeCompare(b.name);
    }

    this.userVisuals.sort(sortFunction);
    this.sharedVisuals.sort(sortFunction);
    this.libraryVisuals.sort(sortFunction);
  }

  private buildDropdown() {
    let categorys = [];

    categorys.push(<HomeItemSelectorCategory>{
      name: 'Dashboards',
      icon: 'fa-th-large',
      subCategories: this.getDashboards()
    })

    categorys.push(<HomeItemSelectorCategory>{
      name: 'Visuals',
      icon: 'fa-table',
      subCategories: this.getVisuals()
    });

    this.dropdownCategories = categorys;

    this.dropdownItem = this.getDropdownItem(this.dashboardId, PublishedReportItemType.Dashboard);
  }

  private getDashboards() {
    let dashboardCategory = [];

    let userDashboardGroup: HomeItemSelectorSubCategory = {
      name: 'My Dashboards',
      icon: 'fa-th-large',
      items: this.userDashboards.filter(d => d.home).map(d => this.dashboardToItem(d))
    };

    let sharedDashboardGroup: HomeItemSelectorSubCategory = {
      name: 'Shared Dashboards',
      icon: 'fa-th-large',
      items: this.sharedDashboards.filter(d => d.home).map(d => this.dashboardToItem(d))
    };

let libraryDashboardGroup: HomeItemSelectorSubCategory = {
      name: 'Library - Dashboards',
      icon: 'fa-th-large',
      items: this.libraryDashboards.filter(d => d.home).map(d => this.dashboardToItem(d))
    };


    if (userDashboardGroup.items.length)
      dashboardCategory.push(userDashboardGroup);

    if (sharedDashboardGroup.items.length)
      dashboardCategory.push(sharedDashboardGroup);

    if (libraryDashboardGroup.items.length)
      dashboardCategory.push(libraryDashboardGroup);

    return dashboardCategory;
  }

  private getVisuals() {
    let visualCategory = [];

    let userVisualGroup: HomeItemSelectorSubCategory = {
      name: 'My Visuals',
      icon: 'fa-table',
      items: this.userVisuals.filter(d => d.home).map(d => this.visualToItem(d))
    }

    let sharedVisualGroup: HomeItemSelectorSubCategory = {
      name: 'Shared Visuals',
      icon: 'fa-table',
      items: this.sharedVisuals.filter(d => d.home).map(d => this.visualToItem(d))
    }

    let libraryVisualGroup: HomeItemSelectorSubCategory = {
      name: 'Library - Visuals',
      icon: 'fa-table',
      items: this.libraryVisuals.filter(d => d.home).map(d => this.visualToItem(d))
    };

    if (userVisualGroup.items.length)
      visualCategory.push(userVisualGroup);

    if (sharedVisualGroup.items.length)
      visualCategory.push(sharedVisualGroup);

    if (libraryVisualGroup.items.length)
      visualCategory.push(libraryVisualGroup);

    return visualCategory;
  }

  private dashboardToItem(dashboard: DashboardListItem): HomeItemSelectorItem {
    return {
      itemId: dashboard.id,
      itemType: PublishedReportItemType.Dashboard,
      name: dashboard.name,
      dashboardPreview: { type: dashboard.type, visuals: dashboard.visuals, layout: dashboard.layout }
    }
  }

  private visualToItem(visual: VisualListItem): HomeItemSelectorItem {
    return {
      itemId: visual.id,
      itemType: PublishedReportItemType.Visual,
      name: visual.name,
      visualPreviewUrl: getInsightsPbiChartType(visual.type)?.previewImageUrl
    }
  }

  private isShowCopy() {
    if (this.libraryDashboards && this.dashboardId) {
      return !!this.libraryDashboards.find(d => d.id === this.dashboardId);
    }
    else if (this.libraryVisuals && this.visualId) {
      return !!this.libraryVisuals.find(v => v.id === this.visualId)
    }
    else {
      return false;
    }
  }

  private canExportToPDF() {
    return !!this.dashboardId;
  }

  private canExportVisual() {
    return !!this.visualId;
  }

  editContent() {
    if(this.selectedDashboardId)
      this.router.navigate(['/dashboards', this.selectedDashboardId, 'edit']);

    if(this.selectedVisualId)
      this.router.navigate(['/visuals', this.selectedVisualId]);
  }

  copy() {
    if(this.selectedDashboardId)
      this.copyDashboardEvent.emit(this.selectedDashboardId);

    if (this.selectedVisualId)
      this.copyVisualEvent.emit(this.selectedVisualId);
  }

  loadContent() {
    if(this.selectedDashboardId)
      this.loadDashboardEvent.emit(this.selectedDashboardId);

    if (this.selectedVisualId)
      this.loadVisualEvent.emit(this.selectedVisualId);
  }

  addDashboardToHome(dashboardId: number) {
    this.addDashboardToHomeEvent.emit(dashboardId);
  }
  addVisualToHome(visualId: number) {
    this.addVisualToHomeEvent.emit(visualId)
  }

  exportToPdf() {
    this._exportToPdf.emit([...(this.filters()?.map(x => x.dto) ?? [])]);
  }
  exportVisual() {
    this._exportVisual.emit();
  }

  selectItem(item: HomeItemSelectorItem) {
    if (item.itemType == PublishedReportItemType.Dashboard) {
      this.selectedDashboardId = item.itemId;
      this.selectedVisualId = null;
    }
    else if (item.itemType == PublishedReportItemType.Visual) {
      this.selectedVisualId = item.itemId;
      this.selectedDashboardId = null;
    }
    this.currentItemLocked();
    this.loadContent();
  }

  currentItemLocked(): void {
    if (!this.selectedDashboardId && !this.selectedVisualId) {
      this.currentItemIsLocked = false;
      return;
    }

    if (this.selectedDashboardId) {
      this.currentItemIsLocked = this.assetLocks.dashboards.isLocked(this.selectedDashboardId);
      return;
    }

    if (this.selectedVisualId) {
      this.currentItemIsLocked = this.assetLocks.visuals.isLocked(this.selectedVisualId);
      return;
    }

    this.currentItemIsLocked = false;
  }

  getDropdownItem(itemId: number, itemType: PublishedReportItemType): HomeItemSelectorItem {
    if (!itemId)
      return null;

    let item: HomeItemSelectorItem = null;

    this.dropdownCategories.find(c => !!c.subCategories.find(sc => !!sc.items.find(i => {
      return (i.itemId == itemId && i.itemType == itemType) ? (item = i) : false;
    })));

    return item;
  }


  readonly dashboardFocused = signal(null);
  readonly visualFocused = signal(false);
  readonly hasFocusedVisual = computed(() =>
    this.dashboard ? !!this.dashboardFocused() : !!this.visualFocused());

  onDashboardFocusedChange(e: any) {
    this.dashboardFocused.set(e);
  }

  onVisualFocusedChange(e: any) {
    this.visualFocused.set(e);
  }

  clearFocused() {
    this.dashboardFocused.set(null);
    this.visualFocused.set(false);
  }
}
